import * as React from 'react';
import { getMouseCanvas, GenericChartComponent } from 'react-financial-charts';
import { drawOnCanvas } from './EdgeCoordinateV3';
import type { MoreProps } from '@/types/commontypes';
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
const ChartComponent = GenericChartComponent as unknown as React.ComponentType<Record<string, unknown>>;
export interface MouseCoordinateYProps {
  readonly arrowWidth?: number;
  readonly at?: 'left' | 'right';
  readonly displayFormat: (value: number) => string;
  readonly dx?: number;
  readonly fontFamily?: string;
  readonly fontSize?: number;
  readonly fill?: string;
  readonly fitToText?: boolean;
  readonly opacity?: number;
  readonly orient?: 'left' | 'right';
  readonly rectWidth?: number;
  readonly rectHeight?: number;
  readonly stroke?: string;
  readonly strokeOpacity?: number;
  readonly strokeWidth?: number;
  readonly textFill?: string;
  readonly yAccessor?: (data: Record<string, unknown>) => number | undefined;
  readonly yAxisPad?: number;
  readonly onSquareClick?: (value: number, event: React.MouseEvent, moreProps: MoreProps) => void;
  readonly showSquareButton?: boolean;
}

export class MouseCoordinateY extends React.Component<MouseCoordinateYProps> {
  public static defaultProps = {
    arrowWidth: 0,
    at: 'right',
    dx: 0,
    fill: '#4C525E',
    fitToText: false,
    fontFamily: "-apple-system, system-ui, Roboto, 'Helvetica Neue', Ubuntu, sans-serif",
    fontSize: 13,
    opacity: 1,
    orient: 'right',
    rectWidth: 50,
    rectHeight: 20,
    strokeOpacity: 1,
    strokeWidth: 1,
    textFill: '#FFFFFF',
    yAxisPad: 0,
    showSquareButton: true,
  };

  public render() {
    return (
      <ChartComponent
        clip={false}
        canvasDraw={this.drawOnCanvas}
        canvasToDraw={getMouseCanvas}
        drawOn={['mousemove', 'pan', 'drag']}
        onClick={this.handleClick}
        isHover={this.isHover} // same pattern as GannBox
        interactiveCursorClass={'react-financial-charts-pointer-cursor'}
        selected={this.isHover}
      />
    );
  }

  private squareBounds: { x: number; y: number; width: number; height: number } | null = null;
  private readonly drawOnCanvas = (ctx: CanvasRenderingContext2D, moreProps: MoreProps) => {
    const props = this.helper(this.props, moreProps);
    if (props === undefined) {
      return;
    }

    const result = drawOnCanvas(ctx, props);
    if (result && result.squareBounds) {
      this.squareBounds = result.squareBounds;
    }
  };

  private readonly handleClick = (_event: React.MouseEvent, moreProps: MoreProps) => {
    const {
      mouseXY,
      chartConfig: { yScale },
    } = moreProps;

    if (this.squareBounds) {
      const [mouseX, mouseY] = mouseXY;
      const { x, y, width, height } = this.squareBounds;
      const dy = y - mouseY;
      const isInsideSquare = mouseX >= x && mouseX <= x + width && mouseY >= y && mouseY <= y + height;
      const invertedYValue = yScale.invert(y - dy);

      // Use toPrecision for small values to preserve significant digits
      // For values < 1, preserve up to 8 significant digits
      // For values >= 1, use toFixed(2) for 2 decimal places
      const roundedValue =
        Math.abs(invertedYValue) < 1 ? Number(invertedYValue.toPrecision(8)) : Number(invertedYValue.toFixed(2));

      if (isInsideSquare) {
        this.props.onSquareClick?.(roundedValue, _event, moreProps);
      }
    }
  };

  private readonly helper = (props: MouseCoordinateYProps, moreProps: MoreProps) => {
    const {
      chartConfig: { yScale },
      chartId,
      currentItem,
      currentCharts,
      mouseXY,
      show,
    } = moreProps;

    if (!show) {
      return undefined;
    }

    if (!mouseXY) {
      return undefined;
    }

    if (!currentCharts || chartId === undefined || currentCharts.indexOf(chartId) < 0) {
      return undefined;
    }

    const { displayFormat, yAccessor } = props;

    if (yAccessor && !currentItem) {
      return undefined;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const y = yAccessor ? yScale(yAccessor(currentItem as unknown as Record<string, unknown>) ?? 0) : mouseXY[1];

    const coordinate = displayFormat(yScale.invert(y));

    return getYCoordinate(y, coordinate, props, moreProps);
  };

  private readonly isHover = (moreProps: MoreProps) => {
    if (!this.squareBounds) return false;

    const { mouseXY } = moreProps;
    if (!mouseXY) return false;

    const [mouseX, mouseY] = mouseXY;
    const { x, y, width, height } = this.squareBounds;
    return mouseX >= x && mouseX <= x + width && mouseY >= y && mouseY <= y + height;
  };
}

export function getYCoordinate(y: number, coordinate: string, props: MouseCoordinateYProps, moreProps: MoreProps) {
  const { width } = moreProps;

  const { orient, at, rectWidth, rectHeight, dx, stroke, strokeOpacity, strokeWidth, showSquareButton } = props;
  const { fill, opacity, fitToText, fontFamily, fontSize, textFill, arrowWidth } = props;

  const x1 = 0;
  const x2 = width ?? 0;
  const edgeAt = at === 'right' ? (width ?? 0) : 0;

  const type = 'horizontal';
  const hideLine = true;

  const coordinateProps = {
    coordinate,
    show: true,
    fitToText,
    type,
    orient: orient ?? 'right',
    edgeAt: edgeAt ?? 0,
    hideLine,
    fill: fill ?? '#4C525E',
    opacity,
    fontFamily: fontFamily ?? "-apple-system, system-ui, Roboto, 'Helvetica Neue', Ubuntu, sans-serif",
    fontSize: fontSize ?? 13,
    textFill: textFill ?? '#FFFFFF',
    stroke,
    strokeOpacity,
    strokeWidth: strokeWidth ?? 1,
    rectWidth: rectWidth ?? 50,
    rectHeight: rectHeight ?? 20,
    arrowWidth: arrowWidth ?? 0,
    dx: dx ?? 0,
    x1,
    x2,
    y1: y,
    y2: y,
    showSquareButton: showSquareButton,
  };

  return coordinateProps;
}
